/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Alchemy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Alchemy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Alchemy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Alchemy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Alchemy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Alchemy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Alchemy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Alchemy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Alchemy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 *Alchemy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* --- Inquiry form: AJAX submit (no page reload) --- */
document.addEventListener('submit', (e) => {
  const form = e.target.closest('.inquiry-modal__form');
  if (!form) return;

  e.preventDefault();

  const submitBtn = form.querySelector('[data-inquiry-submit]');
  if (submitBtn && submitBtn.disabled) return; // photo upload in progress

  const fieldsEl  = form.querySelector('[data-inquiry-fields]');
  const successEl = form.querySelector('[data-inquiry-success]');
  const origText  = submitBtn ? submitBtn.textContent : 'WYŚLIJ';

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Wysyłanie...';
  }

  fetch('/contact', { method: 'POST', body: new FormData(form) })
    .then((res) => {
      const ok = res.ok && (res.url.includes('result%5D=success') || res.url.includes('result]=success'));
      if (ok) {
        if (fieldsEl)  fieldsEl.hidden  = true;
        if (successEl) successEl.hidden = false;
      } else {
        throw new Error();
      }
    })
    .catch(() => {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = origText;
      }
    });
});

/* --- Inquiry form: reset state when modal reopens --- */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-modal^="product-inquiry-"]');
  if (!opener) return;

  const modal = document.getElementById(opener.dataset.modal);
  if (!modal) return;

  const form = modal.querySelector('.inquiry-modal__form');
  if (!form) return;

  const fieldsEl  = form.querySelector('[data-inquiry-fields]');
  const successEl = form.querySelector('[data-inquiry-success]');

  if (fieldsEl)  fieldsEl.hidden  = false;
  if (successEl) successEl.hidden = true;

  form.reset();

  const submitBtn = form.querySelector('[data-inquiry-submit]');
  if (submitBtn)  { submitBtn.disabled = false; submitBtn.textContent = 'WYŚLIJ'; }
});

/* --- Promo banner full-row: match product card row height --- */
function syncFullRowBannerHeight() {
  const banners = document.querySelectorAll('.promo-banner--full-row');
  if (!banners.length) return;

  const isDesktop = window.matchMedia('(min-width: 998px)').matches;

  banners.forEach(banner => {
    if (!isDesktop) {
      banner.style.height = '';
      return;
    }

    let prev = banner.previousElementSibling;
    while (prev && prev.classList.contains('promo-banner')) {
      prev = prev.previousElementSibling;
    }
    if (!prev) return;

    banner.style.height = prev.offsetHeight + 'px';
  });
}

document.addEventListener('DOMContentLoaded', syncFullRowBannerHeight);
window.addEventListener('load', syncFullRowBannerHeight);
window.addEventListener('on:debounced-resize', syncFullRowBannerHeight);

/* --- Growave: ukryj przycisk "POWIADOM O DOSTĘPNOŚCI" dopóki wariant nie jest wybrany --- */
(function () {
  const ATTR = 'data-gw-no-variant';

  const style = document.createElement('style');
  style.textContent = '[gw-stock-fulfilled][' + ATTR + ']{display:none!important}';
  document.head.appendChild(style);

  function isVariantSelected(btn) {
    // Find the closest variant-picker in the same section, or anywhere on the page
    const section = btn.closest('.shopify-section, [data-section]') || document;
    const picker = section.querySelector('variant-picker');
    // No picker = product has no selectable variants → show button immediately
    if (!picker) return true;
    if (typeof picker.getSelectedOptions !== 'function') return false;
    const opts = picker.getSelectedOptions();
    return Array.isArray(opts) && !opts.includes(null);
  }

  function applyHide(btn) {
    if (isVariantSelected(btn)) return; // variant fully chosen — Growave controls visibility
    btn.setAttribute(ATTR, '');
  }

  // MutationObserver — catches buttons injected dynamically by Growave
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute && node.hasAttribute('gw-stock-fulfilled')) applyHide(node);
        node.querySelectorAll && node.querySelectorAll('[gw-stock-fulfilled]').forEach(applyHide);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
  document.querySelectorAll('[gw-stock-fulfilled]').forEach(applyHide);

  // When variant becomes fully selected, reveal any buttons Growave injected
  document.addEventListener('on:variant:change', (event) => {
    const { variant, selectedOptions } = event.detail;
    const allSelected = variant !== null && Array.isArray(selectedOptions) && !selectedOptions.includes(null);
    if (!allSelected) return;

    // Remove our hide attr immediately and after next paint (covers async Growave re-injection)
    const reveal = () => document.querySelectorAll('[gw-stock-fulfilled][' + ATTR + ']').forEach(btn => btn.removeAttribute(ATTR));
    reveal();
    requestAnimationFrame(reveal);
  });
})();

/* --- Barcode block: show/update on variant selection --- */
document.addEventListener('on:variant:change', (event) => {
  const { variant, selectedOptions } = event.detail;
  const allSelected = variant !== null && Array.isArray(selectedOptions) && !selectedOptions.includes(null);

  document.querySelectorAll('[data-barcode-block]').forEach(block => {
    const textEl = block.querySelector('[data-barcode-text]');
    if (!textEl) return;
    if (allSelected && variant.barcode) {
      textEl.textContent = variant.barcode;
      block.style.removeProperty('display');
    } else {
      block.style.display = 'none';
    }
  });
});

/* --- Omnibus: skróć etykietę (karty) + przekreśl cenę (karty i strona produktu) --- */
(function () {
  const FULL = 'Najniższa cena z 30 dni przed obniżką';
  const SHORT = 'Najniższa cena';
  // Matches the price value: digits/commas/periods + optional whitespace + "zł"
  const PRICE_RE = /^(.*?:\s*)([\d,.]+(?: |\s)*zł)(.*)/;

  function processCaption(caption) {
    if (caption.dataset.omnibusProcessed) return;
    caption.dataset.omnibusProcessed = '1';

    const textNode = caption.firstChild;
    if (!textNode || textNode.nodeType !== 3) return;

    const isCard = !!caption.closest('.prod-card__omnibus');
    let text = textNode.textContent;
    if (isCard) text = text.replace(FULL, SHORT);

    // Percentage span present = genuine discount; controls strikethrough + label format
    const pctSpan = caption.querySelector('span');
    const hasPercentage = !!pctSpan;

    if (hasPercentage) {
      // Reformat "(-17.89%)" → "-17%": drop parentheses, truncate to whole number
      const m = pctSpan.textContent.trim().match(/^\(([+-])(\d+(?:[.,]\d+)?)%\)$/);
      if (m) {
        pctSpan.textContent = `${m[1]}${Math.floor(parseFloat(m[2].replace(',', '.')))}%`;
      }
    }

    const match = text.match(PRICE_RE);
    if (!match) { textNode.textContent = text; return; }

    const frag = document.createDocumentFragment();
    frag.appendChild(document.createTextNode(match[1]));
    if (hasPercentage) {
      // Strikethrough only when a discount percentage is visible
      const s = document.createElement('s');
      s.textContent = match[2];
      frag.appendChild(s);
    } else {
      frag.appendChild(document.createTextNode(match[2]));
    }
    if (match[3]) frag.appendChild(document.createTextNode(match[3]));
    textNode.replaceWith(frag);
  }

  const SELECTORS = '.prod-card__omnibus p.caption, #am-omnibus-block p.caption, [id^="am-omnibus-container"] p.caption';

  function processAll(root) {
    (root || document).querySelectorAll(SELECTORS).forEach(processCaption);
  }

  processAll();

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(SELECTORS)) processCaption(node);
        node.querySelectorAll && processAll(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* --- Discount block: copy code to clipboard --- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-copy-code]');
  if (!btn) return;

  const code = btn.dataset.copyCode;
  navigator.clipboard.writeText(code).then(() => {
    btn.setAttribute('data-copied', '');
    setTimeout(() => btn.removeAttribute('data-copied'), 2000);
  });
});

/*  */
(function () {
  const CSS = '@media (max-width: 767px) { .widget.bottom.left.shadow-dialog { left: auto !important; right: 20px !important; } }';

  function inject(shadowRoot) {
    if (shadowRoot.querySelector('style[data-mm-cookie-position]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-mm-cookie-position', '');
    style.textContent = CSS;
    shadowRoot.appendChild(style);
  }

  function tryInject() {
    const host = document.querySelector('pandectes-cmp');
    if (host && host.shadowRoot) {
      inject(host.shadowRoot);
      return true;
    }
    return false;
  }

  if (!tryInject()) {
    new MutationObserver((mutations, observer) => {
      if (tryInject()) observer.disconnect();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
